import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  IconButton,
  Button as MuiButton,
  Grid,
  Paper,
  CircularProgress,
  Chip
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { axiosInstance } from "../component/Api/instance";
import { UserContext } from "../Context";

export default function AttemptReviewModal({ open, onClose, attemptId, paperTitle = "" }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [attemptDetails, setAttemptDetails] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showExplanation, setShowExplanation] = useState(false);
  const [zoomImage, setZoomImage] = useState(false);
  const email = localStorage.getItem("userMail");
  const { Endpoint } = useContext(UserContext) || {};
  // const baseUploadUrl = Endpoint ? `${Endpoint}/controllers/api/admin/upload/` : "https://nursingupdate.in/Nursing/controllers/api/admin/upload/";
  const baseUploadUrl = Endpoint ?
    `${Endpoint}/controllers/api/admin/upload/`
    // : "https://nursingupdate.in/Nursing/controllers/api/admin/upload/";
    : "https://nursingupdate.in/testing_be/controllers/api/admin/upload/";

  useEffect(() => {
    if (open && attemptId) {
      fetchAttemptReview();
    }
  }, [open, attemptId]);

  const fetchAttemptReview = async () => {
    setLoading(true);
    setError(null);
    setCurrentIndex(0);
    setShowExplanation(false);
    try {
      const res = await axiosInstance.post("get/U_GetAttemptReview.php", {
        userId: email,
        attemptId: attemptId,
      });

      if (res.data?.status === "success" && res.data?.attempt) {
        setAttemptDetails(res.data.attempt);
        setQuestions(res.data.attempt.answers || []);
      } else {
        setError(res.data?.message || "Failed to load attempt review.");
      }
    } catch (err) {
      console.error("Error fetching attempt review:", err);
      setError("An error occurred while loading attempt review.");
    } finally {
      setLoading(false);
    }
  };

  const isImage = (url) => {
    return url && typeof url === "string" && url.match(/\.(jpeg|jpg|gif|png|svg|webp|jfif)$/i) != null;
  };

  const currentQ = questions[currentIndex];

  const correctCount = questions.filter(
    (q) => q.student_answer && q.student_answer === q.correct_answer
  ).length;

  const wrongCount = questions.filter(
    (q) => q.student_answer && q.student_answer !== q.correct_answer
  ).length;

  const skippedCount = questions.filter(
    (q) => !q.student_answer || q.student_answer === ""
  ).length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: "20px",
          backgroundColor: "var(--color-background, #f8fafc)",
          color: "var(--color-text-primary, #0f172a)",
        },
      }}
    >
      <DialogTitle
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "18px 24px",
          borderBottom: "1px solid #e2e8f0",
          backgroundColor: "#ffffff",
        }}
      >
        <Box style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          <Typography variant="h6" style={{ fontWeight: 800 }}>
            Attempt Review
          </Typography>
          {attemptDetails?.attempt_number && (
            <Chip
              label={`Attempt #${attemptDetails.attempt_number}`}
              color="primary"
              size="small"
              style={{ fontWeight: 700 }}
            />
          )}
          {attemptDetails && (
            <Chip
              label={`Score: ${attemptDetails.total_marks ?? attemptDetails.final_score} / ${attemptDetails.total_questions}`}
              variant="outlined"
              size="small"
              style={{ fontWeight: 600, borderColor: "#3b82f6", color: "#1d4ed8" }}
            />
          )}
          {paperTitle && (
            <Typography variant="caption" color="textSecondary" style={{ maxWidth: "300px" }} noWrap>
              {paperTitle}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small" style={{ color: "#64748b" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent style={{ padding: "24px", minHeight: "450px" }}>
        {loading ? (
          <Box style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "300px", gap: "16px" }}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="textSecondary">
              Loading attempt answers...
            </Typography>
          </Box>
        ) : error ? (
          <Box style={{ textAlign: "center", padding: "40px" }}>
            <Typography color="error" variant="body1" style={{ fontWeight: 600 }}>
              {error}
            </Typography>
          </Box>
        ) : questions.length === 0 ? (
          <Box style={{ textAlign: "center", padding: "40px" }}>
            <Typography color="textSecondary" variant="body1">
              No saved question review data found for this attempt.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Left Column: Question Details */}
            <Grid item xs={12} lg={8}>
              <Paper
                elevation={0}
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "16px",
                  padding: "24px",
                  border: "1px solid #e2e8f0",
                  minHeight: "480px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                    <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#1e3a8a" }}>
                      Question {currentIndex + 1} of {questions.length}
                    </Typography>

                    {currentQ.student_answer ? (
                      currentQ.student_answer === currentQ.correct_answer ? (
                        <Chip
                          icon={<CheckCircleIcon style={{ color: "#15803d" }} />}
                          label="Correct"
                          size="small"
                          style={{ backgroundColor: "#dcfce7", color: "#15803d", fontWeight: 700 }}
                        />
                      ) : (
                        <Chip
                          icon={<CancelIcon style={{ color: "#b91c1c" }} />}
                          label="Incorrect"
                          size="small"
                          style={{ backgroundColor: "#fee2e2", color: "#b91c1c", fontWeight: 700 }}
                        />
                      )
                    ) : (
                      <Chip
                        icon={<HelpOutlineIcon style={{ color: "#64748b" }} />}
                        label="Skipped"
                        size="small"
                        style={{ backgroundColor: "#f1f5f9", color: "#64748b", fontWeight: 700 }}
                      />
                    )}
                  </Box>

                  <Typography variant="h6" style={{ fontWeight: 700, marginBottom: "20px", lineHeight: 1.5 }}>
                    {currentIndex + 1}. {currentQ.questions}
                  </Typography>

                  {/* Question Image if any */}
                  {currentQ.image && (
                    <Box
                      style={{ display: "flex", justifyContent: "center", marginBottom: "20px", cursor: "zoom-in" }}
                      onClick={() => setZoomImage(!zoomImage)}
                    >
                      <img
                        src={`${baseUploadUrl}${currentQ.image}`}
                        alt="Question Graphic"
                        style={{
                          maxHeight: zoomImage ? "350px" : "180px",
                          maxWidth: "100%",
                          borderRadius: "12px",
                          objectFit: "contain",
                          transition: "all 0.3s ease",
                        }}
                      />
                    </Box>
                  )}

                  {/* Options */}
                  <Box style={{ marginTop: "16px" }}>
                    {["option1", "option2", "option3", "option4"].map((optKey, idx) => {
                      const optVal = currentQ[optKey];
                      if (!optVal) return null;

                      const isCorrect = optVal === currentQ.correct_answer;
                      const isStudent = optVal === currentQ.student_answer;

                      let bgColor = "#ffffff";
                      let borderColor = "#e2e8f0";
                      let badge = null;

                      if (isCorrect) {
                        bgColor = "rgba(34, 197, 94, 0.1)";
                        borderColor = "#22c55e";
                        badge = (
                          <Chip label="Correct Answer" size="small" style={{ backgroundColor: "#22c55e", color: "#fff", fontWeight: 700, height: "20px", fontSize: "0.7rem", marginLeft: "auto" }} />
                        );
                      } else if (isStudent && !isCorrect) {
                        bgColor = "rgba(239, 68, 68, 0.1)";
                        borderColor = "#ef4444";
                        badge = (
                          <Chip label="Your Answer" size="small" style={{ backgroundColor: "#ef4444", color: "#fff", fontWeight: 700, height: "20px", fontSize: "0.7rem", marginLeft: "auto" }} />
                        );
                      }

                      return (
                        <Box
                          key={idx}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "12px 16px",
                            marginBottom: "10px",
                            borderRadius: "12px",
                            border: `2px solid ${borderColor}`,
                            backgroundColor: bgColor,
                            gap: "12px",
                          }}
                        >
                          <Box
                            style={{
                              width: "24px",
                              height: "24px",
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: 700,
                              fontSize: "0.8rem",
                              backgroundColor: isCorrect ? "#22c55e" : isStudent ? "#ef4444" : "#f1f5f9",
                              color: isCorrect || isStudent ? "#fff" : "#475569",
                            }}
                          >
                            {String.fromCharCode(65 + idx)}
                          </Box>

                          {isImage(optVal) ? (
                            <img
                              src={`${baseUploadUrl}${optVal}`}
                              alt={`Option ${idx + 1}`}
                              style={{ maxHeight: "60px", borderRadius: "8px" }}
                            />
                          ) : (
                            <Typography variant="body1" style={{ fontWeight: 550, color: "#1e293b" }}>
                              {optVal}
                            </Typography>
                          )}

                          {badge}
                        </Box>
                      );
                    })}
                  </Box>

                  {/* Explanation Toggle */}
                  {currentQ.explanation && (
                    <Box style={{ marginTop: "16px" }}>
                      <Typography
                        onClick={() => setShowExplanation(!showExplanation)}
                        style={{
                          color: "#2563eb",
                          cursor: "pointer",
                          fontWeight: 700,
                          fontSize: "0.9rem",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        {showExplanation ? "▲ Hide Explanation" : "▼ Show Detailed Explanation"}
                      </Typography>

                      {showExplanation && (
                        <Paper
                          elevation={0}
                          style={{
                            marginTop: "10px",
                            padding: "16px",
                            backgroundColor: "#f8fafc",
                            border: "1px solid #cbd5e1",
                            borderRadius: "12px",
                          }}
                        >
                          <Typography variant="body2" style={{ lineHeight: 1.6, color: "#334155" }}>
                            {currentQ.explanation}
                          </Typography>
                        </Paper>
                      )}
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Right Column: Palette & Navigation */}
            <Grid item xs={12} lg={4}>
              <Paper
                elevation={0}
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "16px",
                  padding: "24px",
                  border: "1px solid #e2e8f0",
                  height: "480px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography variant="subtitle1" style={{ fontWeight: 800, marginBottom: "12px" }}>
                    Question Palette
                  </Typography>

                  <Box style={{ display: "flex", gap: "8px", marginBottom: "16px", flexWrap: "wrap" }}>
                    <Chip label={`✅ Correct: ${correctCount}`} size="small" style={{ backgroundColor: "#dcfce7", color: "#15803d", fontWeight: 700 }} />
                    <Chip label={`❌ Wrong: ${wrongCount}`} size="small" style={{ backgroundColor: "#fee2e2", color: "#b91c1c", fontWeight: 700 }} />
                    <Chip label={`⚪ Skipped: ${skippedCount}`} size="small" style={{ backgroundColor: "#f1f5f9", color: "#475569", fontWeight: 700 }} />
                  </Box>

                  <Box
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px",
                      maxHeight: "300px",
                      overflowY: "auto",
                      padding: "4px",
                    }}
                  >
                    {questions.map((q, idx) => {
                      const isCorrect = q.student_answer && q.student_answer === q.correct_answer;
                      const isWrong = q.student_answer && q.student_answer !== q.correct_answer;

                      let bg = "#f1f5f9";
                      let color = "#475569";
                      let border = "1px solid #cbd5e1";

                      if (isCorrect) {
                        bg = "#22c55e";
                        color = "#ffffff";
                        border = "none";
                      } else if (isWrong) {
                        bg = "#ef4444";
                        color = "#ffffff";
                        border = "none";
                      }

                      if (idx === currentIndex) {
                        border = "3px solid #1e3a8a";
                      }

                      return (
                        <button
                          key={idx}
                          onClick={() => {
                            setCurrentIndex(idx);
                            setShowExplanation(false);
                          }}
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            border: border,
                            fontWeight: 700,
                            backgroundColor: bg,
                            color: color,
                            cursor: "pointer",
                            fontSize: "0.85rem",
                            transition: "all 0.15s ease",
                          }}
                        >
                          {idx + 1}
                        </button>
                      );
                    })}
                  </Box>
                </Box>

                <Box style={{ display: "flex", justifyContent: "space-between", gap: "12px", marginTop: "16px" }}>
                  <MuiButton
                    variant="outlined"
                    color="primary"
                    disabled={currentIndex === 0}
                    onClick={() => {
                      setCurrentIndex(currentIndex - 1);
                      setShowExplanation(false);
                    }}
                    style={{ borderRadius: "10px", flex: 1, textTransform: "none", fontWeight: 700 }}
                  >
                    Previous
                  </MuiButton>
                  <MuiButton
                    variant="contained"
                    color="primary"
                    disabled={currentIndex === questions.length - 1}
                    onClick={() => {
                      setCurrentIndex(currentIndex + 1);
                      setShowExplanation(false);
                    }}
                    style={{ borderRadius: "10px", flex: 1, textTransform: "none", fontWeight: 700 }}
                  >
                    Next
                  </MuiButton>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions style={{ padding: "12px 24px", borderTop: "1px solid #e2e8f0" }}>
        <MuiButton onClick={onClose} variant="contained" color="inherit" style={{ borderRadius: "8px", textTransform: "none", fontWeight: 700 }}>
          Close Review
        </MuiButton>
      </DialogActions>
    </Dialog>
  );
}
